import { isV2Experience } from 'containers/v2/constants';

import { captureSentryError } from './capture';

export const API_ERROR_TYPE = {
  OK_WITH_ERROR: '200_WITH_ERROR',
  CLIENT: '4XX',
  SERVER: '5XX',
} as const;

export type ApiErrorType = typeof API_ERROR_TYPE[keyof typeof API_ERROR_TYPE];

/**
 * Per endpoint, per page load (the counter is in memory). Sentry bills per event,
 * and the ">50 events" alert adds events across users, so a small cap is enough.
 */
export const API_ERROR_MAX_PER_ENDPOINT = 10;

const reportedCount = new Map<string, number>();

const MUTED_STORAGE_KEY = 'sentryMutedApiEndpoints';

const parseMutedPayload = (payload: unknown): string[] => {
  if (typeof payload !== 'string' || payload.trim() === '') return [];
  try {
    const parsed = JSON.parse(payload);
    const list = Array.isArray(parsed) ? parsed : parsed?.endpoints;
    return Array.isArray(list)
      ? list.filter((e): e is string => typeof e === 'string')
      : [];
  } catch (e) {
    return [];
  }
};

const readStoredMuted = (): string[] => {
  try {
    const stored = localStorage.getItem(MUTED_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (e) {
    return [];
  }
};

let mutedEndpoints: Set<string> | null = null;

const getMutedEndpoints = (): Set<string> => {
  if (!mutedEndpoints) mutedEndpoints = new Set(readStoredMuted());
  return mutedEndpoints;
};

/**
 * Called once the feature toggles load. `payload` is the variant payload of
 * SENTRY_MUTED_API_ENDPOINTS; a missing flag or payload clears the list, so
 * removing an endpoint in Unleash un-mutes it. The list is also kept in
 * localStorage so requests fired before the toggles load honour it.
 */
export const setMutedApiEndpoints = (payload: unknown): void => {
  const list = parseMutedPayload(payload);
  mutedEndpoints = new Set(list);
  try {
    if (list.length) localStorage.setItem(MUTED_STORAGE_KEY, JSON.stringify(list));
    else localStorage.removeItem(MUTED_STORAGE_KEY);
  } catch (e) {
    // storage blocked — in-memory list still works
  }
};

export const isEndpointMuted = (endpoint: string): boolean =>
  getMutedEndpoints().has(endpoint);

export const resetApiErrorCounts = (): void => reportedCount.clear();

export const classifyApiError = (
  statusCode?: number | string,
): ApiErrorType | null => {
  const code = Number(statusCode);
  if (!Number.isFinite(code)) return null;
  if (code >= 500) return API_ERROR_TYPE.SERVER;
  if (code >= 400) return API_ERROR_TYPE.CLIENT;
  if (code >= 200 && code < 300) return API_ERROR_TYPE.OK_WITH_ERROR;
  return null;
};

const ID_SEGMENT = /^(\d+|[0-9a-f]{8}-[0-9a-f-]{27,}|[0-9a-f]{24,})$/i;

/** Drops host and query string, and folds ids so one endpoint = one tag value. */
export const toEndpoint = (fetchUrl = ''): string => {
  let pathname = fetchUrl;
  try {
    pathname = new URL(fetchUrl, 'http://placeholder').pathname;
  } catch (e) {
    pathname = fetchUrl.split('?')[0];
  }
  return pathname
    .split('/')
    .map(segment => (ID_SEGMENT.test(segment) ? ':id' : segment))
    .join('/');
};

const ERROR_STATUSES = new Set(['ERROR', 'FAILED', 'FAILURE', 'FAIL']);

/**
 * For direct-response calls, where request() returns the body as-is and does not
 * throw. Only explicit failure shapes count, so valid payloads that happen to
 * carry a `message` or `error: null` are never flagged. Shapes are the ones the
 * dashboard already reads off failed responses (errorMeta.errors / .error /
 * .title + .detail).
 */
export const hasErrorBody = (body: unknown): boolean => {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return false;
  const { status, success, isSuccess, error, errors, title, detail } =
    body as Record<string, unknown>;

  if (typeof status === 'string' && ERROR_STATUSES.has(status.toUpperCase()))
    return true;
  if (success === false || isSuccess === false) return true;
  if (typeof error === 'string' && error.trim() !== '') return true;
  if (
    error &&
    typeof error === 'object' &&
    typeof (error as Record<string, unknown>).message === 'string'
  )
    return true;
  if (Array.isArray(errors) && errors.length > 0) return true;
  // RFC 7807 problem details
  return typeof title === 'string' && typeof detail === 'string';
};

type CaptureApiErrorInput = {
  fetchUrl: string;
  statusCode?: number | string;
  message?: unknown;
  /** Page route the user was on. Defaults to window.location.pathname. */
  pathname?: string;
  /** Overrides the derived endpoint, for URLs whose path carries a free-form key. */
  endpoint?: string;
};

const UNAUTHORIZED = /unauthori[sz]ed|401/i;
// The global Sentry ignore list drops any event whose text has "Unauthorized" or
// "401", so auth failures get neutral text. The status stays in the statusCode tag.
const AUTH_FAILURE_TEXT = 'Authentication failed (token invalid or expired)';

const reportApiError = ({
  fetchUrl,
  statusCode,
  message,
  pathname,
  endpoint: endpointOverride,
}: CaptureApiErrorInput): void => {
  const apiErrorType = classifyApiError(statusCode);
  if (!apiErrorType) return;

  const rawText = typeof message === 'string' ? message.trim() : '';
  const text =
    Number(statusCode) === 401 || UNAUTHORIZED.test(rawText)
      ? AUTH_FAILURE_TEXT
      : rawText;

  const endpoint = endpointOverride ?? toEndpoint(fetchUrl);
  if (isEndpointMuted(endpoint)) return;
  const count = reportedCount.get(endpoint) ?? 0;
  if (count >= API_ERROR_MAX_PER_ENDPOINT) return;
  reportedCount.set(endpoint, count + 1);

  const path =
    pathname ??
    (typeof window !== 'undefined' ? window.location.pathname : undefined);

  captureSentryError(text || `API error ${statusCode} ${endpoint}`, {
    pathname: path,
    crash_origin: 'api',
    label: 'api_error',
    apiEndpoint: endpoint,
    // One issue per endpoint + type, so the ">50 events" alert counts per endpoint.
    fingerprint: ['dashboard', 'api_error', apiErrorType, endpoint],
    extraTags: {
      api_error_type: apiErrorType,
      // Same tag the error boundary sets, so crashes and API errors filter alike.
      v2Page: String(isV2Experience()),
      endpoint,
      // Sentry drops tag values over 200 chars.
      fetchUrl: fetchUrl.slice(0, 200),
      statusCode: String(statusCode),
      // Route priority defaults to medium, which beforeSend samples at 20%.
      incident_priority:
        apiErrorType === API_ERROR_TYPE.SERVER ? 'critical' : 'high',
    },
  });
};

/** Reporting must never change what request() does, so any failure here is dropped. */
// api_error reporting is switched off; flip to true to re-enable.
const API_ERROR_REPORTING_ENABLED = false;

export const captureApiError = (input: CaptureApiErrorInput): void => {
  if (!API_ERROR_REPORTING_ENABLED) return;
  try {
    reportApiError(input);
  } catch (e) {
    // intentionally empty
  }
};

type FetchCallInput = { fetchUrl: string; endpoint?: string };

/**
 * For callers that use makeFetchRequest directly (no request() wrapper): pass the
 * resolved body. makeFetchRequest does not reject on HTTP errors, so the status
 * and error body have to be checked here.
 */
export const captureFetchResponseError = (
  { fetchUrl, endpoint }: FetchCallInput,
  response: { statusCode?: number; message?: unknown; detail?: unknown } | null,
): void => {
  const code = Number(response?.statusCode);
  const failed =
    code >= 400 || (code >= 200 && code < 300 && hasErrorBody(response));
  if (!failed) return;
  captureApiError({
    fetchUrl,
    endpoint,
    statusCode: code,
    message: response?.message || response?.detail,
  });
};

/** For the catch side: makeFetchRequest rejects with statusCode on non-JSON HTTP errors. */
export const captureFetchRejection = (
  { fetchUrl, endpoint }: FetchCallInput,
  err: { statusCode?: number } | null,
): void => {
  if (err?.statusCode) {
    captureApiError({ fetchUrl, endpoint, statusCode: err.statusCode });
  }
};
